import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import { supabase } from '../supabaseClient';
import { fetchProfileNotifications } from '../database/databaseNotifications.js';
import { BellAlertIcon } from '@heroicons/react/24/outline';

const Layout = ({ children, page, hasServices = true }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isReserveActive = location.pathname === '/reserve';
  const isProfileActive = location.pathname === '/profile';

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUser(data.user ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('profiles').select('type').eq('id', user.id).single();
      if (!cancelled) {
        if (error) {
          console.error('Error fetching user role:', error.message);
        } else {
          setRole(data?.type ?? null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Get unread notifications for clients
  useEffect(() => {
    if (!user) return;
    fetchProfileNotifications().then((data) => {
      const hasUnread = data.some((n) => {
        if (!n.notificationsprofile || n.notificationsprofile.length === 0) return true;
        return n.notificationsprofile.some((np) => !np.read_at);
      });
      setUnreadNotifications(hasUnread);
    });
  }, [user]); 

  /* ---------------- Menu + logout ---------------- */
  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    closeMenu();
    navigate('/');
  };

  const layoutPage = page === 'home' ? '' : 'bg-cream';

  return (
    <div className="relative min-h-screen flex flex-col bg-cream font-sans overflow-x-hidden">
      <header className={`absolute md:fixed inset-x-0 top-0 z-20 bg-cream/70 backdrop-blur-sm px-6 py-4 ${layoutPage}`}>
        <div className="mx-auto flex max-w-screen-xl items-center justify-end md:justify-center">
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium tracking-wide text-ink">
            {page === 'home' ? (
              <>
                <ScrollLink to="galeria" smooth duration={500} className="cursor-pointer hover:text-terracotta">
                  GALERÍA
                </ScrollLink>
                {hasServices && (
                  <ScrollLink to="profesionales" smooth duration={500} className="cursor-pointer hover:text-terracotta">
                    SERVICIOS
                  </ScrollLink>
                )}
                <ScrollLink to="contacto" smooth duration={500} className="cursor-pointer hover:text-terracotta">
                  CONTACTO
                </ScrollLink>
              </>
            ) : (
              <RouterLink to="/" className="hover:text-terracotta">
                INICIO
              </RouterLink>
            )}

            <RouterLink to="/reserve" className={`hover:text-terracotta ${isReserveActive ? 'text-terracotta font-semibold' : ''}`}>
              RESERVAR UNA CLASE
            </RouterLink>

            {user && (
              <RouterLink to="/profile" className={`hover:text-terracotta flex items-center gap-1 ${isProfileActive ? 'text-terracotta font-semibold' : ''}`}>
                MI CUENTA
                {unreadNotifications && <BellAlertIcon className="w-6 h-6" />}
              </RouterLink>
            )}

            {user && (role === 'admin' || role === 'staff') && (
              <RouterLink to="/admin" className="hover:text-terracotta">
                ADMIN
              </RouterLink>
            )}

            {/* desktop auth / whatsapp */}
            {user ? (
              <button onClick={handleLogout} className="rounded-full border border-terracotta px-4 py-2 text-ink hover:bg-terracotta hover:text-cream transition">
                CERRAR SESIÓN
              </button>
            ) : (
              <RouterLink to="/login" className="rounded-full border border-terracotta px-4 py-2 text-ink hover:bg-terracotta hover:text-cream transition">
                INICIAR SESIÓN
              </RouterLink>
            )}

            <a
              href="https://wa.me/34642388997?text=Hola%2C%20quiero%20más%20información"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-terracotta px-4 py-2 text-center font-semibold text-cream hover:bg-terracotta-dark transition"
            >
              WHATSAPP
            </a>
          </nav>

          {/* Hamburger (mobile only) */}
          <button className="md:hidden flex flex-col gap-[6px] focus:outline-none" onClick={toggleMenu}>
            <span className={`h-[3px] w-6 bg-ink transition ${menuOpen ? 'translate-y-[9px] rotate-45' : ''}`} />
            <span className={`h-[3px] w-6 bg-ink transition ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`h-[3px] w-6 bg-ink transition ${menuOpen ? '-translate-y-[9px] -rotate-45' : ''}`} />
          </button>
        </div>

        {/* Mobile drawer */}
        <div className={`md:hidden overflow-hidden transition-[max-height] duration-300 ${menuOpen ? 'max-h-screen' : 'max-h-0'}`}>
          <ul className={`flex flex-col gap-4 px-6 py-4 text-lg font-medium text-ink ${layoutPage}`}>
            {page === 'home' ? (
              <>
                <li>
                  <ScrollLink to="galeria" smooth duration={500} onClick={closeMenu}>
                    GALERÍA
                  </ScrollLink>
                </li>
                {hasServices && (
                  <li>
                    <ScrollLink to="profesionales" smooth duration={500} onClick={closeMenu}>
                      SERVICIOS
                    </ScrollLink>
                  </li>
                )}
                <li>
                  <ScrollLink to="contacto" smooth duration={500} onClick={closeMenu}>
                    CONTACTO
                  </ScrollLink>
                </li>
              </>
            ) : (
              <li>
                <RouterLink to="/" onClick={closeMenu}>
                  INICIO
                </RouterLink>
              </li>
            )}
            <li>
              <RouterLink to="/reserve" onClick={closeMenu} className={isReserveActive ? 'text-terracotta font-semibold' : ''}>
                RESERVAR UNA CLASE
              </RouterLink>
            </li>
            {user && (
              <li>
                <RouterLink to="/profile" onClick={closeMenu} className={`flex items-center gap-1 ${isProfileActive ? 'text-terracotta font-semibold' : ''}`}>
                  MI CUENTA
                  {unreadNotifications && (
                    <span className="inline-flex items-center justify-center text-xs font-bold text-cream bg-terracotta rounded-full w-5 h-5">!</span>
                  )}
                </RouterLink>
              </li>
            )}
            {user && (role === 'admin' || role === 'staff') && (
              <li>
                <RouterLink to="/admin" onClick={closeMenu}>
                  ADMIN
                </RouterLink>
              </li>
            )}
            <li className="border-t border-terracotta/30 pt-3">
              {user ? (
                <button onClick={handleLogout} className="w-full text-left text-terracotta-dark">
                  CERRAR SESIÓN
                </button>
              ) : (
                <RouterLink to="/login" onClick={closeMenu}>
                  INICIAR SESIÓN
                </RouterLink>
              )}
            </li>
            <li>
              <a
                href="https://wa.me/34642388997?text=Hola%2C%20quiero%20más%20información"
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-full bg-terracotta py-2 text-center font-semibold text-cream hover:bg-terracotta-dark"
              >
                WHATSAPP
              </a>
            </li>
          </ul>
        </div>
      </header>
      <main className="flex-grow">{children}</main>
      <footer className="bg-terracotta py-6 text-center text-cream text-xs tracking-[0.25em] uppercase">
        Origin · Beyond Pilates
      </footer>
    </div>
  );
};

export default Layout;
